import {useState, useCallback} from "react";
import {CourtBigModel, CourtsRequestModel, CourtTypeEnum} from "@/app/api/courts";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {useNavigate} from "react-router-dom";
import {route} from "@/app/routes/AppRoutes";
import {ReviewModel} from "@/app/api/reviews";

export interface ScreenState {
  hasError?: string;
  selectedFilter: CourtTypeEnum | undefined;
  filters: CourtsRequestModel;
  showFilters: boolean
  showCreateCourt: boolean
  pointSelected: {
    lat: number;
    lon: number;
  } | undefined
}

const useMapWithPoints = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    selectedFilter: undefined,
    filters: {},
    showFilters: false,
    showCreateCourt: false,
    pointSelected: undefined
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const setType = useCallback((
    type: CourtTypeEnum,
    applyFilters: (filters: CourtsRequestModel) => void,
    options?: { applyFilterCount?: boolean }
  ) => {
    let newFilters = {};
    if (state.filters.type === type) {
      newFilters = {
        ...state.filters,
        type: undefined
      }
    } else {
      newFilters = {
        ...state.filters,
        type: type
      }
    }

    const {applyFilterCount = false} = options || {};

    updateState({filters: newFilters});

    if (applyFilterCount) applyFilters(newFilters);
  }, [state.filters, updateState]);

  const setBooking = useCallback((bookingRequired: boolean) => {
    updateState({
      filters: {
        ...state.filters,
        bookingRequired: bookingRequired ? true : undefined
      }
    })
  }, [state.filters, updateState]);

  const setArtificialLight = useCallback((artificialLight: boolean) => {
    updateState({
      filters: {
        ...state.filters,
        artificialLight: artificialLight ? true : undefined
      }
    })
  }, [state.filters, updateState]);

  const setLockerRooms = useCallback((lockerRooms: boolean) => updateState({
    filters: {
      ...state.filters,
      lockerRooms: lockerRooms ? true : undefined
    }
  }), [state.filters, updateState]);

  const setParkingAvailability = useCallback((parkingAvailability: boolean) => updateState({
    filters: {
      ...state.filters,
      parkingAvailability: parkingAvailability ? true : undefined
    }
  }), [state.filters, updateState]);

  const setCoachAvailable = useCallback((coachAvailable: boolean) => updateState({
    filters: {
      ...state.filters,
      coachAvailable: coachAvailable ? true : undefined
    }
  }), [state.filters, updateState]);

  const setSocialVibe = useCallback((socialVibe: boolean) => updateState({
    filters: {
      ...state.filters,
      socialVibe: socialVibe ? true : undefined
    }
  }), [state.filters, updateState]);

  const setHasCafeOrBar = useCallback((hasCafeOrBar: boolean) => updateState({
    filters: {
      ...state.filters,
      hasCafeOrBar: hasCafeOrBar ? true : undefined
    }
  }), [state.filters, updateState]);

  const setRentalGearAvailable = useCallback((rentalGearAvailable: boolean) => updateState({
    filters: {
      ...state.filters,
      rentalGearAvailable: rentalGearAvailable ? true : undefined
    }
  }), [state.filters, updateState]);

  const setMinRating = useCallback((minRating: number | undefined) => {
    updateState({
      filters: {
        ...state.filters,
        minRating
      }
    });

  }, [state.filters, updateState]);

  const clearFilters = useCallback(() => {
    updateState({
      selectedFilter: undefined,
      filters: {}
    })
  }, [updateState]);

  const toggleFilters = useCallback(() => {
    updateState({showFilters: !state.showFilters})
  }, [state.showFilters, updateState]);

  const calculateFilterCount = useCallback(() => {
    let count = 0;
    if (state.filters.type !== undefined) count++;
    if (state.filters.bookingRequired) count++;
    if (state.filters.artificialLight) count++;
    if (state.filters.lockerRooms) count++;
    if (state.filters.parkingAvailability) count++;
    if (state.filters.minRating) count++;
    return count;
  }, [state.filters]);

  const toggleCreateCourt = useCallback(() => {
    updateState({showCreateCourt: !state.showCreateCourt});
  }, [state.showCreateCourt]);

  const setCreatedMarker = useCallback((coords: { lat: number; lon: number } | undefined) => {
    updateState({pointSelected: coords});
  }, [updateState]);

  const goToCreateMatch = useCallback((court?: CourtBigModel, reviews?: ReviewModel[]) => {
    if (court?.id) {
      Cookies.set(CookiesKey.location, JSON.stringify({ courtId: court.id }));
    } else if (state.pointSelected) {
      Cookies.set(CookiesKey.location, JSON.stringify({ coords: state.pointSelected }));
    }
    Cookies.remove(CookiesKey.reviews);
    navigate(route.createAMatch);
  }, [updateState, state.pointSelected]);

  return {
    state,
    setType,
    setBooking,
    setArtificialLight,
    setLockerRooms,
    setParkingAvailability,
    setCoachAvailable,
    setSocialVibe,
    setHasCafeOrBar,
    setRentalGearAvailable,
    setMinRating,
    clearFilters,
    toggleFilters,
    calculateFilterCount,
    toggleCreateCourt,
    setCreatedMarker,
    goToCreateMatch,
  };
};

export default useMapWithPoints;